import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import s from './search-teacher.module.scss'
import classNames from 'classnames'
import { useClickOutside } from '@utils/lib/useClickOutside'
import { debounce } from '@utils/lib/debounce'
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji'
import { Dropdown } from '@shared/ui/dropdown/dropdown'
import { IconCheck, IconChevronDown, IconLayoutGrid, IconPlus, IconX } from '@tabler/icons-react'

const TIME_OPTIONS = [
  { label: 'Доступное время', value: '' },
  { label: 'Утро', value: 'morning' },
  { label: 'День', value: 'day' },
  { label: 'Вечер', value: 'evening' },
]

const DAY_OPTIONS = [
  { label: 'Доступный день', value: '' },
  { label: 'Выходные', value: 'weekends' },
  { label: 'Будни', value: 'weekdays' },
]

const SORT_OPTIONS = [
  { label: 'Без сортировки', value: 'recommended' },
  { label: 'По рейтингу', value: 'rating' },
  { label: 'По популярности', value: 'popularity' },
]

const VISIBLE_INSTRUMENTS_COUNT = 5

const getLabelByValue = (options, value) =>
  options.find((option) => option.value === value)?.label || ''

const getValueByLabel = (options, label) =>
  options.find((option) => option.label === label)?.value || ''

const Checkbox = ({ value, onChange, label }) => (
  <label className={s.checkbox}>
    <span
      className={classNames(s.checkboxBox, value && s.checkboxBoxChecked)}
    >
      {value && <IconCheck size={12} className={s.checkboxIcon} />}
    </span>
    <input
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      className={s.checkboxInput}
    />
    <span className={s.checkboxLabel}>{label}</span>
  </label>
)

const SortDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef(null)
  const selectedLabel =
    SORT_OPTIONS.find((o) => o.value === value)?.label || SORT_OPTIONS[0].label

  useClickOutside(ref, () => setIsOpen(false))

  return (
    <div className={s.sortWrapper} ref={ref}>
      <span className={s.sortLabel}>Сортировка:</span>
      <div className={s.sortDropdown}>
        <button
          type="button"
          className={s.sortButton}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span>{selectedLabel}</span>
          <IconChevronDown
            size={16}
            className={classNames(s.sortArrow, isOpen && s.sortArrowOpen)}
          />
        </button>
        {isOpen && (
          <nav className={s.sortMenu}>
            <ul className={s.sortList}>
              {SORT_OPTIONS.map((option) => (
                <li
                  key={option.value}
                  className={classNames(
                    s.sortItem,
                    option.value === value && s.sortItemSelected
                  )}
                  onClick={() => {
                    onChange(option.value)
                    setIsOpen(false)
                  }}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </div>
  )
}

export const SearchTeacher = ({ onSubmit, control, instruments }) => {
  const [instrumentsExpanded, setInstrumentsExpanded] = useState(false)

  const allInstruments =
    instruments?.flatMap((group) => group?.instruments || []) || []

  const timeLabels = useMemo(() => TIME_OPTIONS.map((o) => o.label), [])
  const dayLabels = useMemo(() => DAY_OPTIONS.map((o) => o.label), [])

  const submit = useCallback(() => debounce(onSubmit, 500), [onSubmit])

  const toggleInstrument = (field, id) => {
    const current = field.value || []
    if (current.includes(id)) {
      field.onChange(current.filter((item) => item !== id))
    } else {
      field.onChange([...current, id])
    }
    submit()
  }

  const clearInstruments = (field) => {
    field.onChange([])
    submit()
  }

  const renderSelect = (name, label, options, labels) => (
    <Controller
      key={name}
      name={name}
      control={control}
      defaultValue=""
      render={({ field }) => {
        const selectedLabel = getLabelByValue(options, field.value)
        return (
          <Dropdown
            label={label}
            options={labels}
            value={selectedLabel}
            onChange={(optionLabel) => {
              const value = getValueByLabel(options, optionLabel)
              field.onChange(value)
              submit()
            }}
            className={s.dropdown}
            buttonClassName={s.dropdownButton}
          />
        )
      }}
    />
  )

  const visibleInstruments = instrumentsExpanded
    ? allInstruments
    : allInstruments.slice(0, VISIBLE_INSTRUMENTS_COUNT)
  const hasMoreInstruments = allInstruments.length > VISIBLE_INSTRUMENTS_COUNT

  return (
    <div className={s.wrapper}>
      <div className={s.filtersRow}>
        {renderSelect('timeOfDay', 'Доступное время', TIME_OPTIONS, timeLabels)}
        {renderSelect('daysType', 'День недели', DAY_OPTIONS, dayLabels)}
      </div>

      <Controller
        name="instruments"
        control={control}
        defaultValue={[]}
        render={({ field }) => {
          const selected = field.value || []
          const isAllSelected = selected.length === 0

          return (
            <div className={s.instrumentsRow}>
              <button
                type="button"
                className={classNames(s.chip, isAllSelected && s.chipActive)}
                onClick={() => clearInstruments(field)}
              >
                <IconLayoutGrid size={16} className={s.chipIcon} />
                <span>Все инструменты</span>
                {isAllSelected && <IconX size={14} className={s.chipClose} />}
              </button>
              {visibleInstruments.map((instrument) => {
                const isSelected = selected.includes(instrument.id)
                return (
                  <button
                    key={instrument.id}
                    type="button"
                    className={classNames(s.chip, isSelected && s.chipActive)}
                    onClick={() => toggleInstrument(field, instrument.id)}
                  >
                    <span className={s.chipText}>
                      <TextWithEmoji text={instrument.name} />
                    </span>
                    {isSelected && <IconX size={14} className={s.chipClose} />}
                  </button>
                )
              })}
              {hasMoreInstruments && (
                <button
                  type="button"
                  className={classNames(
                    s.chip,
                    s.chipMore,
                    instrumentsExpanded && s.chipActive
                  )}
                  onClick={() => setInstrumentsExpanded((prev) => !prev)}
                >
                  <IconPlus size={14} className={s.chipIcon} />
                  <span>Другое</span>
                </button>
              )}
            </div>
          )
        }}
      />

      <div className={s.bottomRow}>
        <Controller
          name="free_first_lesson"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Checkbox
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                submit()
              }}
              label="Первое бесплатное занятие"
            />
          )}
        />
        <Controller
          name="works_with_children"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Checkbox
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                submit()
              }}
              label="Занимается с детьми"
            />
          )}
        />
        <Controller
          name="verified_docs"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Checkbox
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                submit()
              }}
              label="Есть лицензия"
            />
          )}
        />
        <Controller
          name="sort_by"
          control={control}
          defaultValue={SORT_OPTIONS[0].value}
          render={({ field }) => (
            <SortDropdown
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                submit()
              }}
            />
          )}
        />
      </div>
    </div>
  )
}
